var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { ResponseType } from './types';
export class FlowiumApiError extends Error {
    constructor(statusCode, data, message, requestId) {
        super(message);
        this.name = 'FlowiumApiError';
        this.statusCode = statusCode;
        this.data = data;
        this.requestId = requestId;
    }
}
function createRequestId() {
    const randomPart = Math.random().toString(36).slice(2, 14);
    return `sdk-${Date.now().toString(36)}-${randomPart}`;
}
function attachRequestId(error, requestId) {
    if (error && typeof error === 'object') {
        try {
            error.requestId = requestId;
        }
        catch (_a) {
        }
    }
    return error;
}
class FlowiumClient {
    constructor(baseUrl, apiToken, fallbackBaseUrl) {
        this.baseUrl = baseUrl.replace(/\/$/, '');
        this.fallbackBaseUrl = fallbackBaseUrl === null || fallbackBaseUrl === void 0 ? void 0 : fallbackBaseUrl.trim().replace(/\/$/, '');
        this.headers = new Headers({
            'Content-Type': 'application/json'
        });
        if (apiToken) {
            this.setAuth(apiToken);
        }
    }
    isCloudflareBlock(statusCode, data) {
        if (statusCode !== 429)
            return false;
        if (typeof data === 'string' && data.includes('error code: 1027')) {
            return true;
        }
        return false;
    }
    isRecoverableRequestError(error) {
        if (error instanceof TypeError) {
            return true;
        }
        if (error instanceof Error) {
            const msg = error.message.toLowerCase();
            return (msg.includes('failed to fetch') ||
                msg.includes('networkerror') ||
                msg.includes('typeerror:') ||
                msg.includes('load failed'));
        }
        return false;
    }
    performRequest(baseUrl, method, endpoint, options, requestId, params) {
        return __awaiter(this, void 0, void 0, function* () {
            const url = new URL(`${baseUrl}/${endpoint.replace(/^\//, '')}`);
            if (params) {
                Object.entries(params).forEach(([key, value]) => {
                    if (value !== undefined && value !== null) {
                        url.searchParams.append(key, value.toString());
                    }
                });
            }
            const requestHeaders = new Headers(this.headers);
            requestHeaders.set('X-Request-ID', requestId);
            const response = yield fetch(url.toString(), Object.assign(Object.assign({}, options), { method, headers: requestHeaders }));
            const data = yield this.parseResponseBody(response);
            return {
                response,
                data,
                requestId: response.headers.get('X-Request-ID') || requestId,
            };
        });
    }
    setAuth(token, isJwt = false) {
        const prefix = isJwt ? 'Bearer' : 'Token';
        this.headers.set('Authorization', `${prefix} ${token}`);
    }
    setClientRelease(release) {
        const normalized = release.trim();
        if (normalized) {
            this.headers.set('X-Flowium-Release', normalized.slice(0, 128));
        }
        else {
            this.headers.delete('X-Flowium-Release');
        }
    }
    parseResponseBody(response) {
        return __awaiter(this, void 0, void 0, function* () {
            const contentType = response.headers.get('content-type') || '';
            if (contentType.includes('application/json')) {
                try {
                    return yield response.json();
                }
                catch (_a) {
                    return null;
                }
            }
            const text = yield response.text();
            if (!text) {
                return null;
            }
            try {
                return JSON.parse(text);
            }
            catch (_b) {
                return text;
            }
        });
    }
    getErrorMessage(statusCode, data) {
        var _a;
        if (data && typeof data === 'object') {
            const payload = data;
            const error = (_a = payload.error) !== null && _a !== void 0 ? _a : payload.message;
            if (typeof error === 'string' && error.trim()) {
                return error;
            }
            return JSON.stringify(payload);
        }
        if (typeof data === 'string' && data.trim()) {
            return data;
        }
        return `Request failed with status ${statusCode}`;
    }
    request(method, endpoint, options = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { params, allowStatusCodes } = options, restOptions = __rest(options, ["params", "allowStatusCodes"]);
            const requestId = createRequestId();
            let primary;
            try {
                primary = yield this.performRequest(this.baseUrl, method, endpoint, restOptions, requestId, params);
            }
            catch (error) {
                if (this.fallbackBaseUrl && this.isRecoverableRequestError(error)) {
                    try {
                        const fallback = yield this.performRequest(this.fallbackBaseUrl, method, endpoint, restOptions, requestId, params);
                        const { response, data, requestId: fallbackRequestId } = fallback;
                        const isAllowedStatus = (allowStatusCodes === null || allowStatusCodes === void 0 ? void 0 : allowStatusCodes.includes(response.status)) || false;
                        if (!response.ok && !isAllowedStatus) {
                            throw new FlowiumApiError(response.status, data, this.getErrorMessage(response.status, data), fallbackRequestId);
                        }
                        return {
                            statusCode: response.status,
                            data: data,
                        };
                    }
                    catch (fallbackError) {
                        if (fallbackError instanceof FlowiumApiError) {
                            throw fallbackError;
                        }
                    }
                }
                throw attachRequestId(error, requestId);
            }
            if (this.fallbackBaseUrl && this.isCloudflareBlock(primary.response.status, primary.data)) {
                try {
                    const fallback = yield this.performRequest(this.fallbackBaseUrl, method, endpoint, restOptions, requestId, params);
                    const { response, data, requestId: fallbackRequestId } = fallback;
                    const isAllowedStatus = (allowStatusCodes === null || allowStatusCodes === void 0 ? void 0 : allowStatusCodes.includes(response.status)) || false;
                    if (!response.ok && !isAllowedStatus) {
                        throw new FlowiumApiError(response.status, data, this.getErrorMessage(response.status, data), fallbackRequestId);
                    }
                    return {
                        statusCode: response.status,
                        data: data,
                    };
                }
                catch (error) {
                }
            }
            const { response, data } = primary;
            const isAllowedStatus = (allowStatusCodes === null || allowStatusCodes === void 0 ? void 0 : allowStatusCodes.includes(response.status)) || false;
            if (!response.ok && !isAllowedStatus) {
                throw new FlowiumApiError(response.status, data, this.getErrorMessage(response.status, data), primary.requestId);
            }
            return {
                statusCode: response.status,
                data: data
            };
        });
    }
    contextPayload(workspaceId, projectId) {
        return {
            workspace_id: workspaceId,
            project_id: projectId
        };
    }
    register(email, password) {
        return __awaiter(this, void 0, void 0, function* () {
            const { data } = yield this.request('POST', '/user/register', {
                body: JSON.stringify({ email, password })
            });
            return data;
        });
    }
    getUser() {
        return __awaiter(this, void 0, void 0, function* () {
            const { statusCode, data } = yield this.request('GET', '/user', {
                allowStatusCodes: [401, 404]
            });
            if (statusCode === 404)
                return ResponseType.NOT_FOUND;
            if (statusCode === 401)
                return ResponseType.NOT_VERIFIED;
            return data;
        });
    }
    getUserPayments() {
        return __awaiter(this, void 0, void 0, function* () {
            const { data } = yield this.request('GET', '/user/payments');
            return data.payments || [];
        });
    }
    getUserDetails() {
        return __awaiter(this, void 0, void 0, function* () {
            const { data } = yield this.request('GET', '/user');
            return {
                total_storage_used: data.total_storage_used,
                storage_limit: data.storage_limit,
                number_of_files: data.number_of_files,
                plan: data.plan,
            };
        });
    }
    getAvailablePlans() {
        return __awaiter(this, void 0, void 0, function* () {
            const { data } = yield this.request('GET', '/plan/list-plans');
            return data.plans;
        });
    }
    deleteAccount() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.request('DELETE', '/user/account');
        });
    }
    createWorkspace(name) {
        return __awaiter(this, void 0, void 0, function* () {
            const { data } = yield this.request('POST', '/space', {
                body: JSON.stringify({ name: name.trim() })
            });
            return data.workspace_id;
        });
    }
    setWorkspace(name, id) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!name && !id)
                throw new Error('Either workspace name or id must be provided');
            yield this.request('POST', '/space/set', {
                body: JSON.stringify({ name, id })
            });
        });
    }
    deleteWorkspace(workspaceName, workspaceId) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!workspaceName && !workspaceId) {
                throw new Error('Either workspace name or id must be provided');
            }
            yield this.request('DELETE', '/space', {
                body: JSON.stringify({ name: workspaceName, id: workspaceId })
            });
        });
    }
    getWorkspaces() {
        return __awaiter(this, void 0, void 0, function* () {
            const { data } = yield this.request('GET', '/space');
            return data.workspaces;
        });
    }
    workspaceInvite(email, workspaceId) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.request('POST', '/space/invite', {
                body: JSON.stringify({ email, workspace_id: workspaceId })
            });
        });
    }
    workspaceUninvite(email, workspaceId, developerId) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.request('POST', '/space/uninvite', {
                body: JSON.stringify({
                    email,
                    developer_id: developerId,
                    workspace_id: workspaceId,
                })
            });
        });
    }
    getWorkspaceMembers(workspaceId) {
        return __awaiter(this, void 0, void 0, function* () {
            const { data } = yield this.request('GET', '/space/members', {
                params: { workspace_id: workspaceId }
            });
            return data.members;
        });
    }
    createProject(name, workspaceId) {
        return __awaiter(this, void 0, void 0, function* () {
            const { data } = yield this.request('POST', '/space/project', {
                body: JSON.stringify({ name: name.trim(), workspace_id: workspaceId })
            });
            return data.project_id;
        });
    }
    getProjects(workspaceId) {
        return __awaiter(this, void 0, void 0, function* () {
            const { data } = yield this.request('GET', '/space/project', {
                params: { workspace_id: workspaceId }
            });
            return data.projects;
        });
    }
    setProject(projectId, workspaceId) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.request('POST', '/space/project/set', {
                body: JSON.stringify({ project_id: projectId, workspace_id: workspaceId })
            });
        });
    }
    deleteProject(projectId, workspaceId) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.request('DELETE', '/space/project', {
                body: JSON.stringify({ project_id: projectId, workspace_id: workspaceId })
            });
        });
    }
    getPlans() {
        return __awaiter(this, void 0, void 0, function* () {
            const { data } = yield this.request('GET', '/plan');
            return data.plans;
        });
    }
    getPlan(planName) {
        return __awaiter(this, void 0, void 0, function* () {
            const plans = yield this.getPlans();
            const plan = plans.find(p => p.name === planName);
            if (!plan)
                throw new Error('Plan not found');
            return plan;
        });
    }
    getUserPlan() {
        return __awaiter(this, void 0, void 0, function* () {
            const user = yield this.getUser();
            if (typeof user === 'object' && 'plan' in user) {
                return this.getPlan(user.plan);
            }
            throw new Error('Failed to get user plan');
        });
    }
    getStorageLimits() {
        return __awaiter(this, void 0, void 0, function* () {
            const user = yield this.getUser();
            if (typeof user !== 'object' || !('plan' in user)) {
                throw new Error('Failed to get user');
            }
            const plan = yield this.getPlan(user.plan);
            return {
                storageLimitGb: plan.storage_limit_gb,
                fileLimit: plan.file_limit,
                maxFileSizeMb: plan.max_file_size_mb,
                totalStorageUsed: user.total_storage_used,
                numberOfFiles: user.number_of_files
            };
        });
    }
    login(email, password) {
        return __awaiter(this, void 0, void 0, function* () {
            const { data } = yield this.request('POST', '/user/login', {
                body: JSON.stringify({ email, password })
            });
            const token = data.token;
            this.setAuth(token, true);
            return token;
        });
    }
    googleLogin(tokenId) {
        return __awaiter(this, void 0, void 0, function* () {
            const { data } = yield this.request('POST', '/user/login/google', {
                body: JSON.stringify({ tokenId })
            });
            const token = data.token;
            this.setAuth(token, true);
            return token;
        });
    }
    checkLogin() {
        return __awaiter(this, void 0, void 0, function* () {
            const { data, statusCode } = yield this.request('GET', '/user/check-login');
            if (statusCode !== 200) {
                throw new Error('Failed to check login');
            }
            return data;
        });
    }
    getApiToken() {
        return __awaiter(this, void 0, void 0, function* () {
            const { data } = yield this.request('GET', '/user/get-api-token');
            return data.api_token;
        });
    }
    regenerateApiToken() {
        return __awaiter(this, void 0, void 0, function* () {
            const { data } = yield this.request('POST', '/user/regenerate-api-token');
            return data.api_token;
        });
    }
    changePassword(oldPassword, newPassword) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.request('POST', '/user/change-password', {
                body: JSON.stringify({ old_password: oldPassword, new_password: newPassword })
            });
        });
    }
    updateUsername(username) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.request('PATCH', '/user/username', {
                body: JSON.stringify({ username })
            });
        });
    }
    requestPasswordReset(email) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.request('POST', '/user/request-password-reset', {
                body: JSON.stringify({ email })
            });
        });
    }
    resetPassword(token, newPassword) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.request('POST', '/user/reset-password', {
                body: JSON.stringify({ token, new_password: newPassword })
            });
        });
    }
    verifyEmail(token) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.request('POST', '/user/verify', {
                body: JSON.stringify({ token })
            });
        });
    }
    createCheckoutSession(planName) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const { data } = yield this.request('POST', '/plan/checkout', {
                body: JSON.stringify({ plan_name: planName })
            });
            return (_a = data.sessionId) !== null && _a !== void 0 ? _a : null;
        });
    }
    verifySubscription(sessionId) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.request('POST', '/plan/verify', {
                body: JSON.stringify({ session_id: sessionId })
            });
        });
    }
    getStorageUsage() {
        return __awaiter(this, void 0, void 0, function* () {
            const { data } = yield this.request('GET', '/plan/storage-usage');
            return data.total_storage;
        });
    }
    getMetrics() {
        return __awaiter(this, void 0, void 0, function* () {
            const { data } = yield this.request('GET', '/user/metrics');
            return data;
        });
    }
    upload(path, content, contentType, workspaceId, projectId, onProgress, options = {}, onUploadStart) {
        "Upload file content to specified path.";
        return __awaiter(this, void 0, void 0, function* () {
            if (!content && !contentType) {
                throw new Error('Either content or contentType must be provided');
            }
            const signal = options.signal;
            const buffer = typeof content === 'string' ? new TextEncoder().encode(content) : content;
            if (!buffer) {
                throw new Error('Content buffer is required for upload');
            }
            const { statusCode, data } = yield this.request('POST', '/obj/upload', {
                body: JSON.stringify({
                    path,
                    mimetype: contentType || 'application/octet-stream',
                    size_bytes: buffer.byteLength,
                    workspace_id: workspaceId,
                    project_id: projectId
                }),
                signal
            });
            if (statusCode !== 200) {
                throw new Error(`Failed to get upload URL: ${data}`);
            }
            onUploadStart === null || onUploadStart === void 0 ? void 0 : onUploadStart(data.file_id);
            if (signal === null || signal === void 0 ? void 0 : signal.aborted) {
                throw new DOMException('Upload was cancelled', 'AbortError');
            }
            if (buffer.byteLength > data.max_file_size_bytes) {
                throw new Error(`File is too large: ${buffer.byteLength} bytes`);
            }
            return new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                const onAbort = () => {
                    if (xhr.readyState === XMLHttpRequest.DONE)
                        return;
                    xhr.abort();
                    reject(new DOMException('Upload was cancelled', 'AbortError'));
                };
                if (signal === null || signal === void 0 ? void 0 : signal.aborted) {
                    reject(new DOMException('Upload was cancelled', 'AbortError'));
                    return;
                }
                signal === null || signal === void 0 ? void 0 : signal.addEventListener('abort', onAbort, { once: true });
                const cleanup = () => signal === null || signal === void 0 ? void 0 : signal.removeEventListener('abort', onAbort);
                xhr.upload.onprogress = (event) => {
                    if (event.lengthComputable && onProgress) {
                        const percentComplete = Math.round((event.loaded / event.total) * 100);
                        onProgress(percentComplete);
                    }
                };
                xhr.onload = () => {
                    cleanup();
                    if (xhr.status === 200) {
                        resolve({ fileId: data.file_id });
                    }
                    else {
                        reject(new Error('Upload failed'));
                    }
                };
                xhr.onerror = () => {
                    cleanup();
                    reject(new Error('Upload failed'));
                };
                xhr.onabort = () => {
                    cleanup();
                    reject(new DOMException('Upload was cancelled', 'AbortError'));
                };
                xhr.open('PUT', data.upload_url);
                xhr.setRequestHeader('Content-Type', contentType || 'application/octet-stream');
                xhr.send(buffer);
            });
        });
    }
    cancelUpload(fileId, workspaceId, projectId) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.request('POST', '/obj/cancel-upload', {
                body: JSON.stringify(Object.assign({ file_id: fileId }, this.contextPayload(workspaceId, projectId))),
                allowStatusCodes: [200, 404]
            });
        });
    }
    cancelUploadByPath(filePath, workspaceId, projectId) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.request('POST', '/obj/cancel-upload', {
                body: JSON.stringify(Object.assign({ path: filePath }, this.contextPayload(workspaceId, projectId))),
                allowStatusCodes: [200, 404]
            });
        });
    }
    getPublicUploadUrl(pathPrefix, filename, mimetype, tags, sizeBytes) {
        return __awaiter(this, void 0, void 0, function* () {
            const { statusCode, data } = yield this.request('POST', '/obj/public-upload-url', {
                body: JSON.stringify({
                    path_prefix: pathPrefix,
                    filename,
                    mimetype,
                    size_bytes: sizeBytes,
                    tags: tags ? tags.join(',') : ''
                })
            });
            if (statusCode !== 200) {
                throw new Error('Failed to get public upload URL');
            }
            return {
                uploadUrl: data.upload_url,
                maxFileSizeBytes: data.max_file_size_bytes,
                fileId: data.file_id
            };
        });
    }
    getFileVersion(filesList, version) {
        return __awaiter(this, void 0, void 0, function* () {
            const searchParams = new URLSearchParams();
            searchParams.append('files_list', JSON.stringify(filesList));
            if (version)
                searchParams.append('version', version);
            const { data } = yield this.request('GET', `/obj/version?${searchParams.toString()}`);
            return data;
        });
    }
    ls(pathPrefix, page, pageSize, sortBy, sortDir, afterPath, workspaceId, projectId) {
        return __awaiter(this, void 0, void 0, function* () {
            const { data } = yield this.request('GET', '/obj/ls', {
                params: Object.assign({ path_prefix: pathPrefix, page, page_size: pageSize, sort_by: sortBy, sort_dir: sortDir, after_path: afterPath }, this.contextPayload(workspaceId, projectId))
            });
            return data;
        });
    }
    stat(path, version, workspaceId, projectId) {
        return __awaiter(this, void 0, void 0, function* () {
            const { data } = yield this.request('GET', '/obj/stat', {
                params: Object.assign({ path, version }, this.contextPayload(workspaceId, projectId))
            });
            return data;
        });
    }
    createDirectory(path, workspaceId, projectId) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.request('POST', '/obj/mkdir', {
                body: JSON.stringify(Object.assign({ path }, this.contextPayload(workspaceId, projectId)))
            });
        });
    }
    deleteItem(path, isDirectory, workspaceId, projectId) {
        return __awaiter(this, void 0, void 0, function* () {
            const url = isDirectory ? '/obj/rmdir' : '/obj';
            yield this.request('DELETE', url, {
                body: JSON.stringify(Object.assign({ path }, this.contextPayload(workspaceId, projectId)))
            });
        });
    }
    downloadFile(path, version, workspaceId, projectId) {
        return __awaiter(this, void 0, void 0, function* () {
            const params = { path };
            if (version)
                params.version = version;
            if (workspaceId)
                params.workspace_id = workspaceId;
            if (projectId)
                params.project_id = projectId;
            const { data } = yield this.request('GET', '/obj/url', {
                params
            });
            return data;
        });
    }
    preview(path, workspaceId, projectId) {
        return __awaiter(this, void 0, void 0, function* () {
            const url = new URL(`${this.baseUrl}/obj/preview`);
            url.searchParams.append('path', path);
            if (workspaceId)
                url.searchParams.append('workspace_id', workspaceId);
            if (projectId)
                url.searchParams.append('project_id', projectId);
            const response = yield fetch(url.toString(), {
                method: 'GET',
                headers: this.headers
            });
            if (!response.ok) {
                throw new Error(`Failed to get preview: ${response.status}`);
            }
            return response.arrayBuffer();
        });
    }
    copyItem(source, destination, workspaceId, projectId) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.request('POST', '/obj/cp', {
                body: JSON.stringify(Object.assign({ source, destination }, this.contextPayload(workspaceId, projectId)))
            });
        });
    }
    moveItem(source, destination, workspaceId, projectId) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.request('POST', '/obj/mv', {
                body: JSON.stringify(Object.assign({ source, destination }, this.contextPayload(workspaceId, projectId)))
            });
        });
    }
    tagFile(path, tags, workspaceId, projectId) {
        return __awaiter(this, void 0, void 0, function* () {
            const cleanTags = tags.map(tag => tag.trim()).filter(Boolean);
            const { data } = yield this.request('POST', '/obj/tag', {
                body: JSON.stringify(Object.assign({ path, tags: cleanTags }, this.contextPayload(workspaceId, projectId)))
            });
            return data;
        });
    }
    untagFile(path, tags, workspaceId, projectId) {
        return __awaiter(this, void 0, void 0, function* () {
            const cleanTags = tags.map(tag => tag.trim()).filter(Boolean);
            const { data } = yield this.request('POST', '/obj/untag', {
                body: JSON.stringify(Object.assign({ path, tags: cleanTags }, this.contextPayload(workspaceId, projectId)))
            });
            return data;
        });
    }
    restoreFileVersion(path, version, workspaceId, projectId) {
        return __awaiter(this, void 0, void 0, function* () {
            const { data } = yield this.request('POST', '/obj/restore', {
                body: JSON.stringify(Object.assign({ path, version }, this.contextPayload(workspaceId, projectId)))
            });
            return data;
        });
    }
    changeFileAccess(path, access, workspaceId, projectId) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.request('POST', '/obj/access', {
                body: JSON.stringify(Object.assign({ path, access }, this.contextPayload(workspaceId, projectId)))
            });
        });
    }
    describe(path, workspaceId, projectId) {
        return __awaiter(this, void 0, void 0, function* () {
            const { data } = yield this.request('POST', '/obj/ai/describe', {
                body: JSON.stringify(Object.assign({ path }, this.contextPayload(workspaceId, projectId)))
            });
            return data;
        });
    }
    removeBackground(path, workspaceId, projectId) {
        return __awaiter(this, void 0, void 0, function* () {
            const { data } = yield this.request('POST', '/obj/ai/remove-bg', {
                body: JSON.stringify(Object.assign({ path }, this.contextPayload(workspaceId, projectId)))
            });
            return data;
        });
    }
    exists(path, workspaceId, projectId) {
        return __awaiter(this, void 0, void 0, function* () {
            const { data } = yield this.request('GET', '/obj/exists', {
                params: Object.assign({ path }, this.contextPayload(workspaceId, projectId))
            });
            return data.exists;
        });
    }
    search(filters = {}, page = 1, pageSize = 10, workspaceId, projectId) {
        return __awaiter(this, void 0, void 0, function* () {
            const { data } = yield this.request('GET', '/obj/search', {
                params: Object.assign({ query: filters.query, tags: filters.tags ? filters.tags.join(',') : undefined, mimetype: filters.mimetype, file_type: filters.file_type, date_from: filters.date_from, date_to: filters.date_to, color_hex: filters.color_hex, sort_by: filters.sort_by, sort_dir: filters.sort_dir, page, page_size: pageSize }, this.contextPayload(workspaceId, projectId))
            });
            return data;
        });
    }
    createSymlink(source, linkName) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.request('POST', '/obj/symlink', {
                body: JSON.stringify({ source, link_name: linkName })
            });
        });
    }
    getJobs(active = false) {
        return __awaiter(this, void 0, void 0, function* () {
            const { data } = yield this.request('GET', '/jobs', {
                params: { active }
            });
            return data;
        });
    }
    ping() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.request('GET', '/ping');
            return 'pong';
        });
    }
    getStaticFile(path) {
        return __awaiter(this, void 0, void 0, function* () {
            const isAbsoluteUrl = /^https?:\/\//i.test(path);
            const response = yield fetch(isAbsoluteUrl ? path : `${this.baseUrl}/static/${path.replace(/^\/+/, '')}`, isAbsoluteUrl ? undefined : { headers: this.headers });
            if (!response.ok) {
                throw new Error('Failed to get static file');
            }
            return response.arrayBuffer();
        });
    }
    getNotifications(page = 1, pageSize = 20) {
        return __awaiter(this, void 0, void 0, function* () {
            const { data } = yield this.request('GET', '/user/notifications', {
                params: { page, page_size: pageSize }
            });
            return data;
        });
    }
    markNotificationsRead(notificationIds) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.request('POST', '/user/notifications/read', {
                body: JSON.stringify({ notification_ids: notificationIds })
            });
        });
    }
    getNotificationSettings() {
        return __awaiter(this, void 0, void 0, function* () {
            const { data } = yield this.request('GET', '/user/notifications/settings');
            return data;
        });
    }
    updateNotificationSettings(settings) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.request('POST', '/user/notifications/settings', {
                body: JSON.stringify(settings)
            });
        });
    }
    supportTicket(message) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.request('POST', '/support/ticket', {
                body: JSON.stringify({ message })
            });
            return response;
        });
    }
    createWidget(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const { data } = yield this.request('POST', '/widget', {
                body: JSON.stringify(params)
            });
            return data;
        });
    }
    listWidgets() {
        return __awaiter(this, void 0, void 0, function* () {
            const { data } = yield this.request('GET', '/widget/list');
            return data.widgets;
        });
    }
    getWidget(id) {
        return __awaiter(this, void 0, void 0, function* () {
            const { data } = yield this.request('GET', `/widget/${id}`);
            return data;
        });
    }
    updateWidget(id, params) {
        return __awaiter(this, void 0, void 0, function* () {
            const { data } = yield this.request('PATCH', `/widget/${id}`, {
                body: JSON.stringify(params)
            });
            return data;
        });
    }
    deleteWidget(id) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.request('DELETE', `/widget/${id}`);
        });
    }
    regenerateWidgetKey(id) {
        return __awaiter(this, void 0, void 0, function* () {
            const { data } = yield this.request('POST', `/widget/${id}/regenerate-key`);
            return data;
        });
    }
    getAuthToken() {
        const auth = this.headers.get('Authorization');
        if (!auth)
            return null;
        const parts = auth.split(' ');
        return parts.length > 1 ? parts[1] : parts[0];
    }
    _subscribeSSE(path, eventName, onEvent, onError) {
        const token = this.getAuthToken();
        if (!token)
            throw new Error('No auth token available for SSE');
        const source = new EventSource(`${this.baseUrl}/sse/${path}?token=${encodeURIComponent(token)}`);
        source.addEventListener(eventName, (event) => {
            try {
                onEvent(JSON.parse(event.data));
            }
            catch (e) {
                console.error(`Failed to parse SSE ${eventName}:`, e);
            }
        });
        if (onError)
            source.onerror = onError;
        return source;
    }
    subscribeNotifications(onNotification, onError) {
        return this._subscribeSSE('notifications', 'notification', onNotification, onError);
    }
    subscribeJobs(onJobUpdate, onError) {
        return this._subscribeSSE('jobs', 'job_update', onJobUpdate, onError);
    }
}
export default FlowiumClient;
//# sourceMappingURL=flowium.js.map